/* «Escribir con el móvil» sin red local (como el teclado remoto del Fire TV): la tele crea una sesión en el servidor de Filoteca con un código
   corto y un QR; el móvil abre https://…/t/CÓDIGO desde cualquier red y lo que se escribe allí llega al campo de la tele. Este módulo es el lado
   de la tele: crea la sesión, cuenta qué campo se rellena y escucha (long-poll) lo que va llegando. Ver server/relay.js. */

const POLL_FAIL_LIMIT = 6;

/** Dirección del servidor: el propio (en la web la página y el servidor son el mismo sitio). */
export function relayBase() {
    try { return location.origin; } catch (e) { return ''; }
}

const kindOf = (el) => {
    const type = String((el && el.type) || '').toLowerCase();
    if (type === 'password') return 'password';
    if (type === 'search') return 'search';
    if (type === 'url') return 'url';
    if (type === 'email') return 'email';
    if (type === 'number' || type === 'tel') return 'number';
    return 'text';
};

/** Qué campo es (lo que verá el móvil): su rótulo, tipo y valor. */
export function fieldInfo(el) {
    let label = '';
    try {
        label = (el.getAttribute('aria-label') || '').trim();
        if (!label && el.id) { const l = document.querySelector('label[for="' + (window.CSS && CSS.escape ? CSS.escape(el.id) : el.id) + '"]'); if (l) label = l.textContent.trim(); }
        if (!label) { const box = el.closest('.field, .item-inner, .list-input, .input'); const l = box && box.querySelector('label'); if (l) label = l.textContent.trim(); }
        if (!label) label = (el.getAttribute('placeholder') || '').trim();
    } catch (e) { label = ''; }
    return { label: label.slice(0, 120), kind: kindOf(el), value: String(el.value || ''), multiline: el.tagName === 'TEXTAREA' };
}

const TEXT_FIELD = 'input:not([type]), input[type="text"], input[type="password"], input[type="search"], input[type="url"], input[type="email"], input[type="number"], input[type="tel"], textarea';

/** Los campos de texto del formulario al que pertenece `el` (la ventana, el formulario o la página), en su orden, con el índice del propio `el`.
    Así el móvil rellena el formulario entero de una vez (clave, usuario y contraseña de OpenSubtitles, por ejemplo). → { fields: [{ el, info }], current } */
export function formFields(el) {
    let box = null;
    try { box = el.closest('form, .dialog, .popup, .sheet-modal, .popover, .page-current, .page'); } catch (e) { box = null; }
    const visible = (x) => { try { const r = x.getBoundingClientRect(); return r.width > 0 || r.height > 0; } catch (e) { return true; } };
    let list = [];
    if (box) {
        try { list = Array.from(box.querySelectorAll(TEXT_FIELD)).filter((x) => x === el || (!x.disabled && !x.readOnly && !x.hasAttribute('data-no-relay') && visible(x))); } catch (e) { list = []; }
    }
    if (!list.length || list.indexOf(el) === -1) list = [el];
    list = list.slice(0, 12);
    if (list.indexOf(el) === -1) list[0] = el;
    return { fields: list.map((x) => ({ el: x, info: fieldInfo(x) })), current: Math.max(0, list.indexOf(el)) };
}

/** Pone un texto en el campo como si se hubiera tecleado (para que lo noten los formularios y los buscadores). */
export function setFieldValue(el, text) {
    const proto = el.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(proto, 'value');
    if (setter && setter.set) setter.set.call(el, text); else el.value = text;
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
}

/** «Intro» en el campo: envía su formulario o, si no hay, la tecla Intro (buscadores, diálogos). */
export function submitField(el) {
    try { el.blur(); } catch (e) { /* nada */ }
    const form = el.closest && el.closest('form');
    if (form) { if (typeof form.requestSubmit === 'function') form.requestSubmit(); else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true })); return; }
    ['keydown', 'keypress', 'keyup'].forEach((type) => el.dispatchEvent(new KeyboardEvent(type, { key: 'Enter', code: 'Enter', keyCode: 13, which: 13, bubbles: true, cancelable: true })));
}

/**
 * Abre la sesión y empieza a escuchar. `on`: { join(), text(t), submit(), closed(why), error() }. Devuelve { code, token, url, setField(f), stop() }.
 * Lanza si el servidor no responde (la tele sin Internet): quien llama se queda con el teclado de la tele.
 */
export async function startRelay({ base = relayBase(), field = null, fields = null, current = 0, on = {}, fetchImpl = (...a) => fetch(...a), timeout = 8000 } = {}) {
    const withTimeout = (url, init, ms) => {
        const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const timer = setTimeout(() => { if (ctl) ctl.abort(); }, ms);
        return fetchImpl(url, Object.assign({ cache: 'no-store', signal: ctl ? ctl.signal : undefined }, init)).finally(() => clearTimeout(timer));
    };
    const r = await withTimeout(base + '/api/relay/new', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }, timeout);
    const made = await r.json().catch(() => null);
    if (!r.ok || !made || !made.code || !made.token) throw Object.assign(new Error('relay'), { code: (made && made.error) || 'relay' });
    const { code, token } = made;
    const api = (op) => base + '/api/relay/' + code + '/' + op + '?token=' + encodeURIComponent(token);
    let stopped = false;
    let after = 0;
    const post = (op, body) => withTimeout(api(op), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) }, timeout).catch(() => null);
    // Un formulario entero ({ fields, current }) o un campo suelto; el servidor entiende las dos formas.
    if (fields && fields.length) await post('field', { fields, current, label: fields[current] && fields[current].label, kind: fields[current] && fields[current].kind, value: fields[current] && fields[current].value, multiline: !!(fields[current] && fields[current].multiline) });
    else if (field) await post('field', field);

    (async () => {
        let fails = 0;
        while (!stopped) {
            let j = null;
            try {
                const res = await withTimeout(api('poll') + '&after=' + after, { method: 'GET' }, 30000);
                j = res.ok ? await res.json() : null;
                if (res.status === 404) { if (!stopped && on.closed) on.closed('gone'); return; }
            } catch (e) { j = null; }
            if (stopped) return;
            if (!j) {
                fails += 1;
                if (fails >= POLL_FAIL_LIMIT) { if (on.error) on.error(); return; }
                await new Promise((res) => setTimeout(res, 1000 * fails));
                continue;
            }
            fails = 0;
            (j.events || []).forEach((ev) => {
                after = Math.max(after, ev.seq || 0);
                if (stopped) return;
                if (ev.type === 'join' && on.join) on.join();
                else if (ev.type === 'text' && on.text) on.text(String(ev.text || ''), Math.max(0, Math.floor(Number(ev.i)) || 0));
                else if (ev.type === 'submit' && on.submit) on.submit();
            });
            after = Math.max(after, j.seq || 0);
            if (j.closed) { if (on.closed) on.closed(j.closed); return; }
        }
    })();

    return {
        code, token, url: base + '/t/' + code, host: base.replace(/^https?:\/\//, '') + '/t',
        setField: (f) => post('field', f),
        stop() {
            if (stopped) return;
            stopped = true;
            post('close', {});
        }
    };
}
